import { RadioCardGroup } from "@blurb/codex-react";
import { Box, createStyles, Stack } from "@mantine/core";
import { RpiAlert, RpiText } from "@rpi/openapi-core";
import {
  attributesForFiltering,
  invalidateSubsequentSteps,
  ProductCreateNavButtons,
  StepProps,
} from "./ProductCreateNavigator";
import {
  filterByMatchingAttributes,
  findAvailableAttributeValues,
  StandardProduct,
} from "../../hooks/useStandardProducts";

type ChoiceStepProps = StepProps & {
  instructionText?: string;
  noOptionsText?: string;
  noSelectionErrorText?: string;
  standardProducts: StandardProduct[];
  flexDirection?: "row" | "column";
  renderCard: (value: string) => JSX.Element | null;
  renderImage?: (value: string) => JSX.Element | null;
  sortValues?: (values: (string | number | boolean)[]) => (string | number | boolean)[];
  getFilterAttributes?: (state: StepProps["state"]) => Record<string, string | number | boolean | undefined>;
};

const useCardStepStyles = createStyles((theme, { flexDirection }: { flexDirection: "row" | "column" }) => ({
  cardGrid: {
    display: "flex",
    gap: "var(--codex-spacing-2)",
    marginTop: "var(--codex-spacing-8)",
    marginBottom: "var(--codex-spacing-2)",
    flexDirection,
    flexWrap: "wrap",
  },
}));

// Generic card step component
export function ChoiceStep({
  state,
  setState,
  attributeKey,
  instructionText = "Please select one to continue.",
  noOptionsText = "No options available based on previous selections.",
  noSelectionErrorText = "Please make a selection to continue.",
  standardProducts,
  renderCard,
  renderImage,
  flexDirection = "row",
  getFilterAttributes,
  sortValues,
}: ChoiceStepProps) {
  const { classes } = useCardStepStyles({ flexDirection });

  if (!attributeKey) return <RpiAlert>Configuration error: No attribute key.</RpiAlert>;

  // Use custom filter attributes or default to attributesForFiltering
  const filterAttributes = getFilterAttributes ? getFilterAttributes(state) : attributesForFiltering(state);
  const filteredProducts = filterByMatchingAttributes(standardProducts, filterAttributes);
  const displayValues = findAvailableAttributeValues(filteredProducts, attributeKey);
  const sortedValues = sortValues ? sortValues(displayValues) : displayValues;

  const currentValue = state.attributes[attributeKey];
  const handleSelect = (value: string | number | boolean | undefined) => {
    setState((draft) => {
      draft.attributes[attributeKey] = value;
      draft.nextFailed = false;
      invalidateSubsequentSteps(draft, draft.currentStepIndex);
    });
  };

  const handleNext = () => {
    setState((draft) => {
      if (attributeKey && state.attributes[attributeKey]) {
        draft.completedStepIndex = draft.currentStepIndex;
        draft.currentStepIndex += 1;
        draft.nextFailed = false;
      } else {
        draft.nextFailed = true;
      }
    });
  };

  return (
    <Stack spacing={0}>
      <RpiText>{instructionText}</RpiText>

      {displayValues.length === 0 ? (
        <RpiAlert>{noOptionsText}</RpiAlert>
      ) : (
        <RadioCardGroup value={currentValue} onValueChange={(value) => handleSelect(value as typeof currentValue)}>
          <Box className={classes.cardGrid}>
            {sortedValues.map((value) => {
              return renderCard(String(value));
            })}
          </Box>
        </RadioCardGroup>
      )}

      <Box sx={{ marginBottom: "var(--codex-spacing-6)" }}>
        {currentValue && renderImage && renderImage(String(currentValue))}
      </Box>
      {state.nextFailed && !currentValue && <RpiAlert>{noSelectionErrorText}</RpiAlert>}

      <ProductCreateNavButtons state={state} setState={setState} onNext={handleNext} />
    </Stack>
  );
}
